import { DomainError } from '../errors/domain-error';
import { slugifyNewsletterPublicationName } from './marketing-access';

export const NEWSLETTER_SECTIONS_MAX = 12;
export const NEWSLETTER_SECTION_NAME_MAX = 60;
export const NEWSLETTER_SECTION_DESCRIPTION_MAX = 280;

/** Lite podcast episode cap — not a pricing entitlement. */
export const NEWSLETTER_AUDIO_MAX_BYTES = 80 * 1024 * 1024;

export const NEWSLETTER_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp4',
  'audio/ogg',
  'audio/wav',
  'audio/webm',
] as const;

export type NewsletterAudioMimeType = (typeof NEWSLETTER_AUDIO_MIME_TYPES)[number];

export function isNewsletterAudioMimeType(value: string): value is NewsletterAudioMimeType {
  return (NEWSLETTER_AUDIO_MIME_TYPES as readonly string[]).includes(value);
}

export function normalizeNewsletterSectionName(name: string): string {
  const trimmed = name.trim().replace(/\s+/g, ' ');
  if (!trimmed) {
    throw new DomainError('Section name is required');
  }
  return trimmed.slice(0, NEWSLETTER_SECTION_NAME_MAX);
}

export function slugifyNewsletterSectionName(name: string): string {
  const slug = slugifyNewsletterPublicationName(name).slice(0, 60);
  return slug || 'section';
}

export function assertNewsletterSectionsLimit(currentCount: number): void {
  if (currentCount >= NEWSLETTER_SECTIONS_MAX) {
    throw new DomainError(`Publications can have at most ${NEWSLETTER_SECTIONS_MAX} sections`);
  }
}

export function assertNewsletterAudioUpload(input: { mimeType: string; sizeBytes: number }): void {
  if (!isNewsletterAudioMimeType(input.mimeType)) {
    throw new DomainError('Unsupported audio format. Use MP3, M4A, OGG, WAV, or WebM.');
  }
  if (input.sizeBytes < 1 || input.sizeBytes > NEWSLETTER_AUDIO_MAX_BYTES) {
    throw new DomainError('Audio must be 80 MB or smaller');
  }
}

export function formatPodcastItunesDuration(durationSeconds?: number | null): string | null {
  if (durationSeconds == null || !Number.isFinite(durationSeconds) || durationSeconds < 1) {
    return null;
  }
  const total = Math.round(durationSeconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${minutes}:${pad(seconds)}`;
}
